/**
 * Onboarding Agent (simulated)
 * ─────────────────────────────
 * Walks the user through the registered free-text fields and captures
 * customer-specific values into the `field_suggestions` catalog. From this
 * point on, those values appear as default options in the corresponding
 * dropdowns (alongside the hardcoded starters and observed-in-data values).
 *
 * This UI is the stand-in for what will eventually be a real LLM-driven
 * onboarding chat. The script + flow are deliberately structured so the
 * same backend endpoints will work when the agent becomes autonomous —
 * only the question-asking layer changes.
 *
 * Persisted via:
 *   GET  /api/field-suggestions/_meta             → list of fields to ask about
 *   GET  /api/field-suggestions/{key}             → values already known
 *   POST /api/field-suggestions/{key}             → write a new agent value
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Chip } from "@mui/material";

import { api } from "@/lib/api";
import { fieldSuggestionKeys, fieldSuggestionsQueryOptions } from "@/lib/queries";
import type { FieldSuggestion } from "@/lib/types";
import { useToast } from "@/components/Toaster";

export const Route = createFileRoute("/_auth/onboarding/")({
  component: OnboardingPage,
});

interface FieldKeyMeta {
  field_key: string;
  title: string;
  description: string;
  onboarding_prompt: string | null;
}

type ChatTurn =
  | { kind: "agent"; text: string; chips?: FieldSuggestion[] }
  | { kind: "user"; text: string }
  | { kind: "system"; text: string };

const META_QUERY_KEY = ["field-suggestions", "_meta"] as const;

function OnboardingPage() {
  const qc = useQueryClient();
  const toast = useToast();

  // Load list of field-keys we know how to onboard
  const metaQuery = useQuery({
    queryKey: META_QUERY_KEY,
    queryFn: () => api<FieldKeyMeta[]>("/api/field-suggestions/_meta"),
    staleTime: 5 * 60_000,
  });

  // Conversation state
  const [stepIndex, setStepIndex] = useState(0);
  const [transcript, setTranscript] = useState<ChatTurn[]>([]);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const meta = metaQuery.data ?? [];
  const currentField = meta[stepIndex];
  const isComplete = !!meta.length && stepIndex >= meta.length;

  // Suggestions for the current question — to show what's already there
  const currentSuggestions = useQuery({
    ...fieldSuggestionsQueryOptions(currentField?.field_key ?? ""),
    enabled: !!currentField,
  });

  // First-time greeting + first prompt
  useEffect(() => {
    if (transcript.length === 0 && metaQuery.data && currentField) {
      setTranscript([
        {
          kind: "agent",
          text:
            "Hi — I'm your onboarding agent. I'll ask a few short questions to " +
            "tailor the dropdown menus to how your business actually operates. " +
            "Anything you tell me here pre-populates the relevant field for everyone.",
        },
        { kind: "agent", text: currentField.onboarding_prompt ?? currentField.title },
      ]);
    }
  }, [metaQuery.data, currentField, transcript.length]);

  // When the field changes, append the new prompt + show known values
  useEffect(() => {
    if (!currentField || transcript.length === 0) return;
    const lastIsCurrentPrompt = transcript.some(
      (t) =>
        t.kind === "agent" &&
        t.text === (currentField.onboarding_prompt ?? currentField.title),
    );
    if (!lastIsCurrentPrompt) {
      setTranscript((prev) => [
        ...prev,
        {
          kind: "agent",
          text: currentField.onboarding_prompt ?? currentField.title,
          chips: currentSuggestions.data,
        },
      ]);
    }
  }, [currentField, transcript, currentSuggestions.data]);

  // Auto-scroll to bottom on new turn
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    inputRef.current?.focus();
  }, [transcript.length]);

  // Mutation: POST a single suggestion
  const addSuggestion = useMutation({
    mutationFn: async (vars: { field_key: string; value: string; label?: string }) =>
      api(`/api/field-suggestions/${encodeURIComponent(vars.field_key)}`, {
        method: "POST",
        body: {
          value: vars.value,
          label: vars.label,
          added_by: "onboarding_agent",
        },
      }),
  });

  function parseValues(input: string): { value: string; label: string }[] {
    // Accepts comma- or newline-separated entries. "Wire Transfer" becomes
    // {value: "wire_transfer", label: "Wire Transfer"}.
    return input
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map((label) => ({
        value: label
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "_")
          .replace(/^_|_$/g, "") || label,
        label,
      }));
  }

  async function submit() {
    if (!currentField) return;
    const text = draft.trim();
    if (!text) return;
    const entries = parseValues(text);
    setDraft("");
    setTranscript((prev) => [...prev, { kind: "user", text }]);

    // Skip handling
    if (text.toLowerCase() === "skip" || text === "—") {
      setTranscript((prev) => [
        ...prev,
        { kind: "agent", text: "No problem — skipping this one. We can revisit later." },
      ]);
      advance();
      return;
    }

    let added = 0;
    for (const entry of entries) {
      try {
        await addSuggestion.mutateAsync({
          field_key: currentField.field_key,
          value: entry.value,
          label: entry.label,
        });
        added++;
      } catch (err) {
        toast.error(`Couldn't save "${entry.label}": ${(err as Error).message}`);
      }
    }
    qc.invalidateQueries({ queryKey: fieldSuggestionKeys.byKey(currentField.field_key) });
    setTranscript((prev) => [
      ...prev,
      {
        kind: "agent",
        text: added > 0
          ? `Got it — saved ${added} value${added === 1 ? "" : "s"} to ${currentField.title}. ` +
            "These will now appear as defaults in the relevant dropdown."
          : "Nothing was saved this time.",
      },
    ]);
    advance();
  }

  function advance() {
    const nextIdx = stepIndex + 1;
    setStepIndex(nextIdx);
    if (nextIdx >= meta.length) {
      setTranscript((prev) => [
        ...prev,
        {
          kind: "agent",
          text:
            "That's all I needed for now. You can always edit any of these from " +
            "Settings → Field Suggestions, and I'll learn new values when you or " +
            "the AI typing in a contract uses them.",
        },
      ]);
    }
  }

  function restart() {
    setStepIndex(0);
    setTranscript([]);
    setDraft("");
  }

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
          Onboarding Agent
          <span className="ml-2 text-xs font-normal bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
            Simulation
          </span>
        </h1>
        <p className="text-sm text-slate-600 mt-1 dark:text-slate-300">
          Stand-in for an LLM-driven onboarding chat. Answers persist into the
          same <code className="text-xs">field_suggestions</code> table that the
          real agent will write to.
        </p>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_320px] overflow-hidden">
        {/* Chat column */}
        <div className="flex flex-col border-r border-slate-200 bg-slate-50 dark:bg-gray-950 dark:border-gray-800">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-3">
            {transcript.map((turn, i) => (
              <ChatBubble key={i} turn={turn} />
            ))}
            {metaQuery.isLoading && (
              <div className="text-sm text-slate-400 dark:text-slate-500">Loading questions…</div>
            )}
            {isComplete && (
              <div className="pt-4">
                <button
                  onClick={restart}
                  className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5"
                >
                  Run again
                </button>
              </div>
            )}
          </div>

          {!isComplete && (
            <div className="border-t border-slate-200 bg-white px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 dark:border-gray-700"
                  placeholder="Type comma-separated values, or 'skip'"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      void submit();
                    }
                  }}
                  disabled={addSuggestion.isPending}
                />
                <button
                  onClick={() => void submit()}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md px-4"
                  disabled={addSuggestion.isPending || !draft.trim()}
                >
                  Send
                </button>
              </div>
              <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Tip: separate multiple answers with commas. e.g. <em>credit card, ACH, wire transfer</em>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar — what we know so far */}
        <aside className="bg-white p-6 overflow-y-auto dark:bg-gray-900">
          <h2 className="text-sm font-semibold text-slate-700 mb-3 dark:text-slate-300">
            Progress
          </h2>
          <ol className="space-y-2 mb-6">
            {meta.map((m, i) => (
              <li
                key={m.field_key}
                className={
                  i < stepIndex
                    ? "text-green-700 text-sm"
                    : i === stepIndex
                    ? "text-blue-700 font-medium text-sm"
                    : "text-slate-400 text-sm"
                }
              >
                {i < stepIndex ? "✓ " : i === stepIndex ? "▸ " : "○ "}
                {m.title}
              </li>
            ))}
          </ol>

          {currentField && currentSuggestions.data && (
            <>
              <h3 className="text-xs font-semibold uppercase text-slate-500 tracking-wide mb-2 dark:text-slate-400">
                Currently in {currentField.title}
              </h3>
              <SuggestionsList suggestions={currentSuggestions.data} />
            </>
          )}
        </aside>
      </div>
    </div>
  );
}

function ChatBubble({ turn }: { turn: ChatTurn }) {
  if (turn.kind === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[70%] bg-blue-600 text-white rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap">
          {turn.text}
        </div>
      </div>
    );
  }
  if (turn.kind === "system") {
    return (
      <div className="text-center text-xs text-slate-500 italic dark:text-slate-400">{turn.text}</div>
    );
  }
  return (
    <div className="flex justify-start">
      <div className="max-w-[70%] bg-white border border-slate-200 rounded-2xl px-4 py-2 text-sm text-slate-800 whitespace-pre-wrap dark:bg-gray-900 dark:border-gray-800 dark:text-slate-200">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-blue-600 mb-1">
          Agent
        </div>
        <div>{turn.text}</div>
        {turn.chips && turn.chips.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {turn.chips.slice(0, 6).map((s) => (
              <Chip
                key={`${s.source}:${s.value}`}
                size="small"
                label={s.label}
                variant={s.source === "starter" ? "outlined" : "filled"}
                color={s.source === "agent" ? "primary" : "default"}
              />
            ))}
            {turn.chips.length > 6 && (
              <span className="text-xs text-slate-500 self-center dark:text-slate-400">
                +{turn.chips.length - 6} more
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SuggestionsList({ suggestions }: { suggestions: FieldSuggestion[] }) {
  const grouped = useMemo(() => {
    const out: Record<FieldSuggestion["source"], FieldSuggestion[]> = {
      starter: [],
      agent: [],
      observed: [],
    };
    suggestions.forEach((s) => out[s.source].push(s));
    return out;
  }, [suggestions]);

  return (
    <div className="space-y-3">
      {(["starter", "agent", "observed"] as const).map((src) =>
        grouped[src].length > 0 ? (
          <div key={src}>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1 dark:text-slate-500">
              {src === "starter"
                ? "Defaults"
                : src === "agent"
                ? "Added by agent"
                : "Seen in your data"}
            </div>
            <ul className="space-y-1">
              {grouped[src].map((s) => (
                <li key={s.value} className="flex items-center justify-between text-sm">
                  <span className="text-slate-700 dark:text-slate-300">{s.label}</span>
                  {s.usage_count > 0 && (
                    <span className="text-xs text-slate-400 dark:text-slate-500">{s.usage_count}×</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ) : null,
      )}
      {suggestions.length === 0 && (
        <div className="text-sm text-slate-400 dark:text-slate-500">No values yet.</div>
      )}
    </div>
  );
}
